import { JourneyInfo } from '@js/utilities/journey'
import { useRouter } from 'vue-router'

// lodash components
import indexOf from 'lodash/indexOf'
import _get from 'lodash/get'
import every from 'lodash/every'
import filter from 'lodash/filter'

export default {
    /**
     * Get the current steps available (and taken) based on conditions up until a condition is not met yet
     * @returns JourneyInfo
     */
    journeyInfo: state => {
        if (!state?.journey) {
            return null
        }

        const journey = new JourneyInfo(state.journey.data)

        for (let i = state.fieldLoadedEvents.length - 1; i >= 0; i--) {
            const fieldEvent = state.fieldLoadedEvents[i]
            const field = journey.getField(fieldEvent.name)

            if (fieldEvent.runOnce) {
                state.fieldLoadedEvents.splice(i, 1)
            }

            if (field) {
                fieldEvent.function(field)
            }
        }

        return journey
    },
    currentSection: (state, getters) => {
        return state.currentRouteStep?.section
    },
    firstUncompletedStep: (state, getters) => {
        return getters.journeyInfo.getFirstUncompletedStep()
    },
    getFirstStep: (state, getters) => {
        return getters.journeyInfo.getFirstStep()
    },
    isFirstStep: (state, getters) => {
        return getters.journeyInfo.isFirstStep()
    },
    stepByName: (state, getters) => name => {
        return getters.journeyInfo.getStepByName(name)
    },
    sectionCount: (state, getters) => {
        return getters.journeyInfo.getSectionCountMost()
    },
    isPathTrajectoryDefined: (state, getters) => {
        return getters.journeyInfo.isPathTrajectoryDefined()
    },
    firstSection: (state, getters) => {
        return getters.journeyInfo.firstSection()
    },
    getTotalStepsCurrentPath: (state, getters) => {
        return getters.journeyInfo.getTotalStepsCurrentPath()
    },
    getCompletedStepsCurrentPath: (state, getters) => {
        return getters.journeyInfo.getCompletedStepsCurrentPath()
    },
    getStepBefore: (state, getters) => step => {
        return getters.journeyInfo.getStepBefore(step)
    },
    getStepAfter: (state, getters) => step => {
        return getters.journeyInfo.getStepAfter(step)
    },
    getPreviousResult: state => reference => {
        let previousResultReference = null

        if (state.resultReferences.length) {
            const currentResultIndex = indexOf(state.resultReferences, reference)

            if (currentResultIndex > 0) {
                previousResultReference = state.resultReferences[currentResultIndex - 1]
            }
        }

        return previousResultReference
    },
    smallSummaryFieldsFilled: (state, getters) => {
        if (!getters.firstSection?.children?.length) {
            return false
        }

        let canShow = true
        for (const step of getters.firstSection.children) {
            if (!canShow) {
                break
            }

            const fields = step?.fields ?? {}
            for (const fieldName in fields) {
                const stepField = fields[fieldName]

                if (!stepField.hasOwnProperty('value') || stepField?.value === null) {
                    canShow = false
                    break
                }
            }
        }

        return canShow
    },
    hasAnySummaryData: (state, getters) => {
        return state.has.smallSummary || state.has.fullSummary
    },
    hasFullSummaryData: (state, getters) => {
        return state.has.fullSummary
    },
    hasSmallSummaryData: (state, getters) => {
        return state.has.smallSummary
    },
    dataField:
        state =>
        (fieldName, fieldContainer = 'data', ignoreErrors = false) => {
            const dataContainer = _get(state, fieldContainer)
            for (const key in dataContainer) {
                const dataField = dataContainer[key]
                if (key == fieldName || dataField?.mappedKey == fieldName) {
                    return dataField
                }
            }

            if (!ignoreErrors) {
                console.error(`No data field name found by the name: ${fieldContainer}.${fieldName}`)
            }

            return null
        },
    // Get field in journey otherwise fallback to a data field
    field:
        (state, getters) =>
        (fieldName, fallbackFieldContainer = 'data', preferFallback = false, ignoreErrors = false) => {
            const preferredField = preferFallback
                ? getters.dataField(fieldName, fallbackFieldContainer, ignoreErrors)
                : getters.journeyInfo?.getField(fieldName)

            if (!preferredField) {
                const secondaryField = preferFallback
                    ? getters.journeyInfo?.getField(fieldName)
                    : getters.dataField(fieldName, fallbackFieldContainer, ignoreErrors)

                if (!secondaryField && !ignoreErrors) {
                    console.error('No field name found in current point of journey by the name: ' + fieldName)
                }

                return secondaryField
            }

            return preferredField
        },
    fieldValue:
        (state, getters) =>
        (fieldName, fallbackFieldContainer = 'data', preferFallback = false, ignoreErrors = false) => {
            return getters.field(fieldName, fallbackFieldContainer, preferFallback, ignoreErrors)?.value || null
        },
    fieldServerError: (state, getters) => fieldName => {
        return getters.journeyInfo?.getField(fieldName)?.serverError
    },
    allFields: (state, getters) => {
        const journeyPath = getters.journeyInfo.getCurrentJourneyPath()
        const combinedFields = {}

        for (const step of journeyPath) {
            const fields = step?.fields || {}
            for (const fieldName in fields) {
                const field = fields[fieldName]
                combinedFields[fieldName] = field
            }
        }

        return { ...state.data, ...combinedFields }
    },
    allFieldValues: (state, getters) => {
        const combinedFields = {}

        const allFields = getters.allFields
        for (const fieldName in allFields) {
            const field = allFields[fieldName]
            const mappingKey = field && field.hasOwnProperty('mappedKey') ? field.mappedKey : fieldName
            combinedFields[mappingKey] = field.hasOwnProperty('value') ? field.value : field
        }

        //add iovation blackbox to fields
        combinedFields.black_box = window.IGLOO?.getBlackbox()?.blackbox

        return combinedFields
    },
    allFieldStepsComplete: (state, getters) => {
        return every(
            filter(getters.journeyInfo.getCurrentJourneyPath(), function (step) {
                return !!step?.fields
            }),
            { completed: true },
        )
    },
    fieldValidationRules: (state, getters) => fieldName => {
        return getters.field(fieldName)?.validationRules || {}
    },
    applyRoutesLoaded: state => {
        return state.applyRoutesLoaded
    },
    loanAmountRange: state => {
        return state.loanAmountRange
    },
    stepViaRouteName: (state, getters) => route => {
        const allSteps = getters.journeyInfo.getCurrentJourneyPath()

        for (const step of allSteps) {
            if (step.route.name == route) {
                return step
            }
        }

        return null
    },
}
